import { useCallback, useEffect, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import {
  FilterKeys,
  FilterLabels,
  FilterParams,
  FilterSelections,
  toFilterLabels,
  toFilterParams,
  toInitialFilters,
} from './listFilterOptions';

export const FILTER_PARAM_PREFIX = 'filter.';
export const SESSION_KEY_PREFIX = 'list-filters:';

const NO_FILTER_KEYS: FilterKeys = {};

/** Request values allowed in the URL, keyed by filter name. Identifiers and enums only, never names or free text. */
export type UrlFilterValues = Record<string, string[]>;

export interface ListSort {
  value: string;
  direction: string;
}

/** Everything a list remembers for the current tab session, keyed by page. Never reaches the URL. */
export interface ListSessionState {
  search?: string;
  page?: number;
  sort?: ListSort;
  /** FilterBar selections for filters that are not allowed in the URL. */
  filters?: FilterSelections;
  filterLabels?: FilterLabels;
  /** The URL filters this state was saved under; a different URL resets page and search. */
  urlFilters?: UrlFilterValues;
}

interface UseListFiltersOptions {
  /** Filter names allowed in the URL. Define as a module constant. */
  urlKeys: string[];
  /** How to read each filter's options. Define as a module constant. */
  filterKeys?: FilterKeys;
  /** Session key; defaults to the pathname. Pass one to share memory across routes. */
  storageKey?: string;
}

export interface UseListFiltersResult {
  /** Current request values. */
  filters: FilterParams;
  search: string;
  /** Current page, owned by the hook; pass to ListModule as `activePage`. */
  page: number;
  /** Current sort, owned by the hook; pass to ListModule as `activeSort`. */
  sort?: ListSort;
  /** Seed for the FilterBar. */
  initialFilters: Record<string, unknown>;
  /** Maps a FilterBar selection to request values and records it in the URL and session. */
  selectFilters: (selections: FilterSelections) => FilterParams;
  setSearch: (search: string) => void;
  /** Sets and remembers the page; pass to ListModule as `setActivePage`. */
  setPage: (page: number) => void;
  /** Sets and remembers the sort; pass to ListModule as `setActiveSort`. */
  setSort: (sort: ListSort) => void;
  /** Opens another list with `values` in its URL; pass the destination's `storageKey` if it uses one. */
  navigateWithFilters: (pathname: string, values: UrlFilterValues, labels?: FilterLabels, storageKey?: string) => void;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const sessionKey = (key: string) => `${SESSION_KEY_PREFIX}${key}`;

export const readListSession = (key: string): ListSessionState => {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(sessionKey(key)) ?? 'null');
    return isRecord(stored) ? stored : {};
  } catch {
    return {};
  }
};

export const writeListSession = (key: string, patch: Partial<ListSessionState>): void => {
  try {
    sessionStorage.setItem(sessionKey(key), JSON.stringify({ ...readListSession(key), ...patch }));
  } catch {
    // Storage can be unavailable or full; the list still works without memory.
  }
};

export const clearListFilters = (): void => {
  try {
    Object.keys(sessionStorage)
      .filter(key => key.startsWith(SESSION_KEY_PREFIX))
      .forEach(key => sessionStorage.removeItem(key));
  } catch {
    // Nothing to clear.
  }
};

export const parseUrlFilters = (search: string, urlKeys: string[]): UrlFilterValues => {
  const params = new URLSearchParams(search);
  return urlKeys.reduce<UrlFilterValues>((values, key) => {
    const found = params.getAll(`${FILTER_PARAM_PREFIX}${key}`).filter(Boolean);
    if (found.length) values[key] = found;
    return values;
  }, {});
};

/** Opens another list with `values` in its URL, for callers outside a filtered list; pass the destination's `storageKey` if it uses one. */
export const navigateWithFilters = (
  navigate: NavigateFunction,
  pathname: string,
  values: UrlFilterValues,
  labels?: FilterLabels,
  storageKey?: string
): void => {
  if (labels) {
    const destinationKey = storageKey ?? pathname;
    writeListSession(destinationKey, {
      filterLabels: { ...readListSession(destinationKey).filterLabels, ...labels },
    });
  }
  navigate({ pathname, search: buildFilterSearch('', values) });
};

export const buildFilterSearch = (search: string, values: FilterParams): string => {
  const params = new URLSearchParams(search);
  Array.from(new Set(params.keys()))
    .filter(key => key.startsWith(FILTER_PARAM_PREFIX))
    .forEach(key => params.delete(key));
  Object.entries(values).forEach(([key, list]) => {
    list.forEach(value => params.append(`${FILTER_PARAM_PREFIX}${key}`, String(value)));
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

const toUrlFilterValues = (params: FilterParams): UrlFilterValues =>
  Object.entries(params).reduce<UrlFilterValues>((values, [key, list]) => {
    values[key] = list.map(String);
    return values;
  }, {});

interface InitialState {
  urlFilters: UrlFilterValues;
  filters: FilterParams;
  search: string;
  initialFilters: Record<string, unknown>;
  page?: number;
  sort?: ListSort;
}

const readInitialState = (key: string, search: string, urlKeys: string[], filterKeys: FilterKeys): InitialState => {
  const stored = readListSession(key);
  const fromUrl = parseUrlFilters(search, urlKeys);
  const hasUrlFilters = Object.keys(fromUrl).length > 0;
  const urlFilters = hasUrlFilters ? fromUrl : (stored.urlFilters ?? {});
  const arrivedElsewhere = hasUrlFilters && !isEqual(fromUrl, stored.urlFilters ?? {});

  return {
    urlFilters,
    filters: { ...toFilterParams(stored.filters ?? {}, filterKeys), ...urlFilters },
    search: arrivedElsewhere ? '' : (stored.search ?? ''),
    initialFilters: { ...stored.filters, ...toInitialFilters(urlFilters, filterKeys, stored.filterLabels) },
    page: arrivedElsewhere ? undefined : stored.page,
    sort: stored.sort,
  };
};

export const useListFilters = ({
  urlKeys,
  filterKeys = NO_FILTER_KEYS,
  storageKey,
}: UseListFiltersOptions): UseListFiltersResult => {
  const location = useLocation();
  const navigate = useNavigate();
  const key = storageKey ?? location.pathname;

  const [initial] = useState(() => readInitialState(key, location.search, urlKeys, filterKeys));
  const [filters, setFilters] = useState<FilterParams>(initial.filters);
  const [search, setSearchState] = useState(initial.search);
  const [page, setPageState] = useState(initial.page ?? 1);
  const [sort, setSortState] = useState(initial.sort);

  useEffect(() => {
    writeListSession(key, { urlFilters: initial.urlFilters, search: initial.search, page: initial.page });
    // Runs once: the initial state is read on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urlFilters = toUrlFilterValues(pick(filters, urlKeys));
    const urlHasFilters = Object.keys(parseUrlFilters(location.search, urlKeys)).length > 0;
    if (urlHasFilters || !Object.keys(urlFilters).length) return;
    navigate(
      { pathname: location.pathname, search: buildFilterSearch(location.search, urlFilters) },
      { replace: true }
    );
  }, [filters, location.pathname, location.search, navigate, urlKeys]);

  const selectFilters = useCallback(
    (selections: FilterSelections) => {
      const params = toFilterParams(selections, filterKeys);
      if (isEqual(params, filters)) return params;

      const urlFilters = toUrlFilterValues(pick(params, urlKeys));
      setFilters(params);
      setPageState(1);
      writeListSession(key, {
        filters: omit(selections, urlKeys),
        filterLabels: toFilterLabels(selections, filterKeys),
        urlFilters,
        page: 1,
      });
      navigate(
        { pathname: location.pathname, search: buildFilterSearch(location.search, urlFilters) },
        { replace: true }
      );
      return params;
    },
    [filterKeys, filters, key, location.pathname, location.search, navigate, urlKeys]
  );

  const setSearch = useCallback(
    (nextSearch: string) => {
      setSearchState(nextSearch);
      setPageState(1);
      writeListSession(key, { search: nextSearch, page: 1 });
    },
    [key]
  );

  const setPage = useCallback(
    (nextPage: number) => {
      setPageState(nextPage);
      writeListSession(key, { page: nextPage });
    },
    [key]
  );

  const setSort = useCallback(
    (nextSort: ListSort) => {
      setSortState(nextSort);
      writeListSession(key, { sort: nextSort });
    },
    [key]
  );

  const navigateToList = useCallback(
    (pathname: string, values: UrlFilterValues, labels?: FilterLabels, storageKey?: string) =>
      navigateWithFilters(navigate, pathname, values, labels, storageKey),
    [navigate]
  );

  return {
    filters,
    search,
    page,
    sort,
    initialFilters: initial.initialFilters,
    selectFilters,
    setSearch,
    setPage,
    setSort,
    navigateWithFilters: navigateToList,
  };
};
